import React, { useState, useEffect, useRef } from 'react';
import s from './ModalStepsTab.module.sass';
import { loadLocalConfig, saveLocalConfig, getLocalConfigsList } from '../../../utils/configUtils';
import { notifyError, notifyWarning, notifySuccess } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';

// Функция для получения network и chain на основе криптовалюты
const getNetworkAndChain = (crypto) => {
    const cryptoLower = (crypto || 'ETH').toLowerCase();
    const mapping = {
        'eth': { network: 'ERC-20', chain: 'Ethereum' },
        'ethereum': { network: 'ERC-20', chain: 'Ethereum' },
        'bnb': { network: 'BEP-20', chain: 'Binance Smart Chain' },
        'binance': { network: 'BEP-20', chain: 'Binance Smart Chain' },
        'usdc': { network: 'ERC-20', chain: 'Ethereum' },
        'btc': { network: 'BTC', chain: 'Bitcoin' },
        'bitcoin': { network: 'BTC', chain: 'Bitcoin' }
    };
    return mapping[cryptoLower] || { network: 'ERC-20', chain: 'Ethereum' };
};

const ModalStepsTab = () => {
    const [modalTemplates, setModalTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [currentConfigFile, setCurrentConfigFile] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadModalTemplates();
    }, []);

    const loadModalTemplates = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Получаем список конфигов и находим активный
            const configs = await getLocalConfigsList();
            if (configs.length === 0) {
                setError('Конфиги не найдены');
                return;
            }

            // Пробуем найти активный конфиг из localStorage или используем первый
            let activeConfigFile = localStorage.getItem('currentConfigFile') || configs[0]?.filename;
            
            // Если конфиг не найден, используем первый доступный
            if (!activeConfigFile && configs.length > 0) {
                activeConfigFile = configs[0].filename;
            }

            if (!activeConfigFile) {
                setError('Не найден активный конфиг');
                return;
            }

            setCurrentConfigFile(activeConfigFile);

            // Загружаем конфиг
            const configData = await loadLocalConfig(activeConfigFile);
            setModalTemplates(configData.modalTemplates || []);
        } catch (err) {
            console.error('Ошибка загрузки шаблонов модалов:', err);
            setError('Не удалось загрузить шаблоны модалов: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const saveModalTemplates = async (updatedTemplates, newTemplate = null) => {
        if (!currentConfigFile) {
            throw new Error('Не выбран конфиг для сохранения');
        }

        try {
            setSaving(true);
            
            // Загружаем текущий конфиг
            const configData = await loadLocalConfig(currentConfigFile);
            
            // Если это новый модал, выполняем автогенерацию
            if (newTemplate) {
                await autoGenerateModalResources(configData, newTemplate);
            }
            
            // Обновляем modalTemplates
            configData.modalTemplates = updatedTemplates;
            
            // Сохраняем конфиг
            const result = await saveLocalConfig(currentConfigFile, configData);
            
            if (!result.success) {
                throw new Error(result.error || 'Ошибка сохранения');
            }

            // Обновляем локальное состояние
            setModalTemplates(updatedTemplates);
            
            // Сохраняем сигнал об обновлении в localStorage для App.js
            // App.js проверит этот сигнал при следующей загрузке и перезагрузит конфиг
            localStorage.setItem('modalTemplatesUpdated', JSON.stringify({
                configFile: currentConfigFile,
                timestamp: Date.now()
            }));
            
            return result;
        } catch (err) {
            console.error('Ошибка сохранения шаблонов модалов:', err);
            throw err;
        } finally {
            setSaving(false);
        }
    };

    // Автогенерация ресурсов для нового модала
    const autoGenerateModalResources = async (configData, template) => {
        const actionKey = template.actionKey;
        const modalKey = `custom_${actionKey}`;

        // 1. Генерация полей в Финансах для каждого тарифа (если указано financeFieldName)
        if (template.financeFieldName) {
            const tariffLabels = ['💰 1 ТАРИФ', '❗❗ 2 ТАРИФ', '❗❗❗ 3 ТАРИФ', '❗❗❗❗ 4 ТАРИФ', '⚠️ VIP ТАРИФ'];
            // Формируем ключ поля в формате tariffXxxAmounts (с 's' на конце, как у существующих)
            const fieldKey = `tariff${actionKey.charAt(0).toUpperCase() + actionKey.slice(1)}Amounts`;

            tariffLabels.forEach(tariffLabel => {
                if (!configData[fieldKey]) {
                    configData[fieldKey] = {};
                }
                if (configData[fieldKey][tariffLabel] === undefined) {
                    configData[fieldKey][tariffLabel] = 0;
                }
            });
        }

        // 2. Генерация кошелька
        if (!configData.modalWallets) {
            configData.modalWallets = {};
        }
        if (!configData.modalWallets[modalKey]) {
            configData.modalWallets[modalKey] = {
                address: template.wallet?.address || '',
                qrValue: template.wallet?.qrValue || template.wallet?.address || '',
                network: template.wallet?.network || '',
                chain: template.wallet?.chain || ''
            };
        }

        return configData;
    };

    const handleCreateNew = () => {
        setEditingTemplate(null);
        setShowEditor(true);
    };

    const handleEdit = async (template) => {
        // Если это встроенный модал - загружаем его HTML/CSS из шаблона
        if (template.isBuiltIn) {
            // Загружаем конфиг чтобы получить переводы и настройки
            const configData = await loadLocalConfig(currentConfigFile || localStorage.getItem('currentConfigFile') || '');
            
            // Получаем HTML/CSS шаблон для этого модала
            const defaultTemplate = getDefaultModalTemplate(template.id, configData);
            setEditingTemplate({
                ...template,
                ...defaultTemplate,
                isBuiltIn: true
            });
        } else {
            setEditingTemplate(template);
        }
        setShowEditor(true);
    };
    
    // Функция для получения HTML/CSS шаблона дефолтного модала
    // Извлекает реальные HTML/CSS из App.js и App.module.sass
    const getDefaultModalTemplate = (modalId, configData) => {
        const templates = {
            commission: {
                actionKey: 'commission',
                icon: '💰',
                enabled: true,
                emailEnabled: true,
                financeFieldName: 'Размер комиссии',
                htmlTemplate: `<div class="cryptoModalOverlay">
    <div class="cryptoModalContainer">
        <div class="cryptoModalHeader">
            <div class="cryptoModalTitle">{title}</div>
            <button class="cryptoModalClose">✕</button>
        </div>
        <div class="cryptoModalContent">
            <div class="cryptoModalDetails">
                <div class="cryptoModalDetailsWrapper">
                    <div class="cryptoModalDetailsContent">
                        <div><strong>{nameLabel}:</strong> {name}</div>
                        <div style="display: flex; align-items: center;">
                            <strong>{bankLabel}:</strong>
                            <span style="margin-left: 8px;">{bank}</span>
                            <img src="{bankIcon}" alt="{bank}" style="width: 20px; height: 20px; margin-left: 8px;" />
                        </div>
                        <div><strong>{accountLabel}:</strong> {accountNumber}</div>
                        <div><strong>{amountLabel}:</strong> {currency} {withdrawalAmount}</div>
                        <div><strong>{dateLabel}:</strong> {paymentDateTime}</div>
                    </div>
                    <img src="{detailsImage}" alt="Coms icon" class="cryptoModalDetailsImage" />
                </div>
            </div>
            <div class="cryptoModalInstruction">
                {instruction}
            </div>
            <div class="cryptoModalFeeAndQrWrapper">
                <div class="cryptoModalFeeSection" style="text-align: center;">
                    <div class="cryptoModalFeeTitle">{paymentRequired}</div>
                    <div class="cryptoModalFeeAmount">{currency} {feeAmount}</div>
                    <div style="color: #64748b; font-size: 12px; font-weight: 400; margin-top: 4px; font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;">
                        {percentageNote}
                    </div>
                </div>
                <div class="cryptoModalQrCode">
                    {qrCode}
                </div>
            </div>
            <div class="cryptoModalPaymentSection">
                <div class="cryptoModalPaymentTitle">{walletTitle}</div>
                <div class="cryptoModalAddressWrapper">
                    <input class="cryptoModalAddressInput" type="text" value="{walletAddress}" readonly />
                    <button class="cryptoModalCopyButton">Копировать</button>
                    <div class="cryptoModalNetworkInfo">{networkInfo}</div>
                </div>
            </div>
            <div class="cryptoModalFooterNote">
                {footerNote}
            </div>
        </div>
        <div class="cryptoModalFooter">
            <button class="cryptoModalCancelButton">{cancelButton}</button>
            <button class="cryptoModalVerifyButton">{paidButton}</button>
        </div>
    </div>
</div>`,
                cssStyles: `.cryptoModalOverlay {
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    position: fixed;
    top: 0;
    left: 0;
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5000;
    animation: modernFadeIn 0.3s ease-in-out;
}

.cryptoModalContainer {
    width: 600px;
    max-width: 95%;
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    box-shadow: 0 4px 20px rgba(43, 108, 176, 0.15);
    animation: modernSlideUp 0.4s ease-out;
    padding: 0;
    border: 1px solid #e2e8f0;
    transition: background 0.3s ease, border-color 0.3s ease;
}

.cryptoModalHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e2e8f0;
    background: #f8fafc;
    transition: background 0.3s ease, border-color 0.3s ease;
}

.cryptoModalTitle {
    color: #e53e3e;
    font-size: 20px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 600;
    letter-spacing: -0.02em;
}

.cryptoModalClose {
    background: rgba(71, 85, 105, 0.1);
    border: none;
    font-size: 16px;
    color: #4a5568;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.cryptoModalClose:hover {
    background: rgba(71, 85, 105, 0.2);
    color: #2d3748;
}

.cryptoModalContent {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    transition: color 0.3s ease;
}

.cryptoModalInstruction {
    font-size: 14px;
    line-height: 1.6;
    color: #4a5568;
    text-align: center;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.cryptoModalDetails {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 16px;
    margin-top: 8px;
    transition: background 0.3s ease, border-color 0.3s ease;
}

.cryptoModalDetailsWrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.cryptoModalDetailsContent {
    font-size: 14px;
    color: #4a5568;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.cryptoModalDetailsImage {
    width: 120px;
    height: auto;
    max-height: 120px;
    object-fit: contain;
}

.cryptoModalFeeAndQrWrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
}

.cryptoModalFeeSection {
    text-align: left;
    flex: 1;
}

.cryptoModalFeeTitle {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 8px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.cryptoModalFeeAmount {
    font-size: 24px;
    font-weight: 700;
    color: #2b6cb0;
    background: #ebf8ff;
    padding: 8px 72px;
    border-radius: 6px;
    display: inline-block;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
    border: 1px solid rgba(43, 108, 176, 0.1);
}

.cryptoModalPaymentSection {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.cryptoModalPaymentTitle {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 0px;
    margin-top: -10px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.cryptoModalAddressInput {
    flex: 1;
    padding: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 14px;
    color: #2d3748;
    text-align: center;
    outline: none;
    cursor: default;
    word-break: break-all;
    height: 40px;
    font-family: 'JetBrains Mono', 'Monaco', 'Cascadia Code', monospace;
    transition: border-color 0.2s ease;
}
.cryptoModalAddressInput:focus {
    border-color: #2b6cb0;
}

.cryptoModalCopyButton {
    background: #2b6cb0;
    border: 1px solid #2b6cb0;
    border-radius: 6px;
    padding: 0 16px;
    font-size: 14px;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translateY(0px);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 500;
    min-width: 40px;
    box-sizing: border-box;
}
.cryptoModalCopyButton:hover {
    background: #2c5aa0;
    transform: translateY(0px) scale(1.02);
}
.cryptoModalCopyButton img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
}

.cryptoModalAddressWrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    position: relative;
    margin-bottom: 25px;
}

.cryptoModalNetworkInfo {
    font-size: 13px;
    color: #4a5568;
    text-align: center;
    margin-top: 5px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    position: absolute;
    top: 100%;
    left: 0;
    right: calc(8px + 80px);
}

.cryptoModalQrCode {
    display: flex;
    justify-content: center;
}
.cryptoModalQrCode canvas {
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.cryptoModalFooterNote {
    font-size: 13px;
    line-height: 1.6;
    color: #4a5568;
    text-align: center;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.cryptoModalFooter {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
    transition: background 0.3s ease, border-color 0.3s ease;
}

.cryptoModalCancelButton {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #4a5568;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    transition: all 0.2s ease;
}
.cryptoModalCancelButton:hover {
    background: #f1f5f9;
    border-color: #cbd5e0;
    transform: translateY(-1px);
}

.cryptoModalVerifyButton {
    background: #2b6cb0;
    color: #ffffff;
    border: 1px solid #2b6cb0;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    transition: all 0.2s ease;
}
.cryptoModalVerifyButton:hover {
    background: #2c5aa0;
    border-color: #2c5aa0;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(43, 108, 176, 0.2);
}

@keyframes modernFadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes modernSlideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}`
            },
            verification: {
                actionKey: 'verification',
                icon: '✅',
                enabled: true,
                emailEnabled: true,
                financeFieldName: 'Сумма верификации',
                htmlTemplate: `<div class="authModalOverlay">
    <div class="authModalContainer">
        <div class="authModalHeader">
            <div class="authModalTitle">
                <i class="fas fa-shield-check" style="margin-right: 10px; font-size: 20px; color: #ffffff;"></i>
                {title}
            </div>
            <button class="authModalClose">✕</button>
        </div>
        <div class="authModalAccentBar"></div>
        <div class="authModalContent">
            <div class="authModalTopSection">
                <img src="{verifImage}" alt="Verification icon" class="authModalVerifImage" />
                <div class="authModalDetailsCard">
                    <div class="authModalDetailsTitle">{withdrawalDetails}</div>
                    <div class="authModalDetailsContent">
                        <div class="authModalDetailsRow">
                            {nameLabel}
                            <span><strong>{name}</strong></span>
                        </div>
                        <div class="authModalDetailsRow">
                            {bankLabel}
                            <div style="display: flex; align-items: center;">
                                <span><strong>{bank}</strong></span>
                            </div>
                        </div>
                        <div class="authModalDetailsRow">
                            {accountLabel}
                            <span><strong>{accountNumber}</strong></span>
                        </div>
                        <div class="authModalDetailsRow">
                            {amountLabel}
                            <span><strong>{currency} {withdrawalAmount}</strong></span>
                        </div>
                        <div class="authModalDetailsRow">
                            {dateLabel}
                            <span><strong>{paymentDateTime}</strong></span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="authModalInstruction">
                {instruction}
            </div>
            <div class="authModalFeeAndNoteWrapper">
                <div class="authModalVerificationNote">
                    {verificationNote}
                </div>
                <div class="authModalFeeSection">
                    <div class="authModalFeeTitle">{paymentRequired}</div>
                    <div class="authModalFeeAmount">{currency} {feeAmount}</div>
                </div>
            </div>
            <div class="authModalPaymentSection">
                <div class="authModalPaymentTitle">{walletTitle}</div>
                <div class="authModalPaymentWrapper">
                    <div class="authModalQrCode">
                        {qrCode}
                    </div>
                    <div class="authModalAddressBlock">
                        <input class="authModalAddressInput" type="text" value="{walletAddress}" readonly />
                        <button class="authModalCopyButton">Копировать</button>
                    </div>
                </div>
                <div class="authModalNetworkInfo">{networkInfo}</div>
            </div>
        </div>
        <div class="authModalFooter">
            <button class="authModalCancelButton">{cancelButton}</button>
            <button class="authModalVerifyButton">{paidButton}</button>
        </div>
    </div>
</div>`,
                cssStyles: `.authModalOverlay {
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    position: fixed;
    top: 0;
    left: 0;
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5000;
    animation: modernFadeIn 0.3s ease-in-out;
}

.authModalContainer {
    width: 600px;
    max-width: 95%;
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    box-shadow: 0 4px 20px rgba(43, 108, 176, 0.15);
    padding: 0;
    border: 1px solid #e2e8f0;
    animation: modernSlideUp 0.4s ease-out;
    transition: background 0.3s ease, border-color 0.3s ease;
}

.authModalHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: #2b6cb0;
    color: #ffffff;
}

.authModalTitle {
    font-size: 18px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 600;
    letter-spacing: -0.02em;
}

.authModalClose {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    font-size: 1.25rem;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.authModalClose:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
}

.authModalAccentBar {
    height: 3px;
    background: linear-gradient(90deg, #2b6cb0, #3182ce);
    width: 100%;
}

.authModalContent {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    transition: color 0.3s ease;
}

.authModalTopSection {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
}

.authModalVerifImage {
    width: 100px;
    height: 100px;
    border-radius: 0px;
    object-fit: contain;
    box-shadow: 0 0px 0px rgba(0, 0, 0, 0.05);
    margin-top: 50px;
}

.authModalDetailsCard {
    flex: 1;
    background: linear-gradient(to right, #ffffff 0%, #edf2f7 40%, #edf2f7 30%);
    border-radius: 8px;
    padding: 12px;
    font-size: 13px;
    color: #2d3748;
    transition: background 0.3s ease, color 0.3s ease;
}

.authModalDetailsTitle {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 8px;
    text-align: center;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.authModalDetailsContent {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.authModalDetailsRow {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.authModalInstruction {
    font-size: 14px;
    line-height: 1.5;
    color: #4a5568;
    text-align: center;
    background: #f8fafc;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.authModalFeeAndNoteWrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin: 12px 0;
}

.authModalVerificationNote {
    font-size: 14px;
    line-height: 1.5;
    color: #4a5568;
    flex: 1;
    text-align: left;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.authModalFeeSection {
    text-align: center;
    flex: 1;
}

.authModalFeeTitle {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 8px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.authModalFeeAmount {
    font-size: 22px;
    font-weight: 700;
    color: #16a085;
    background: #f0fdfa;
    border: 1px solid rgba(22, 160, 133, 0.1);
    padding: 8px 12px;
    border-radius: 6px;
    display: block;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    letter-spacing: -0.01em;
    font-variant-numeric: tabular-nums;
}

.authModalPaymentSection {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.authModalPaymentTitle {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
    text-align: center;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.authModalPaymentWrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 12px;
    border-radius: 8px;
}

.authModalQrCode {
    display: flex;
    justify-content: center;
}
.authModalQrCode canvas {
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.authModalAddressBlock {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.authModalAddressInput {
    padding: 10px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 13px;
    color: #2d3748;
    text-align: center;
    outline: none;
    cursor: default;
    word-break: break-all;
    height: 36px;
    font-family: 'JetBrains Mono', 'Monaco', 'Cascadia Code', monospace;
    transition: border-color 0.2s ease;
}
.authModalAddressInput:focus {
    border-color: #2b6cb0;
}

.authModalCopyButton {
    background: #2b6cb0;
    border: 1px solid #2b6cb0;
    border-radius: 6px;
    padding: 0 14px;
    font-size: 13px;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 500;
    min-width: 36px;
    box-sizing: border-box;
}
.authModalCopyButton:hover {
    background: #2c5aa0;
    transform: scale(1.02);
}
.authModalCopyButton img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
}

.authModalNetworkInfo {
    font-size: 13px;
    color: #4a5568;
    text-align: center;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.authModalFooter {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 20px;
    border-top: 1px solid #e2e8f0;
    background: #f8fafc;
    transition: background 0.3s ease, border-color 0.3s ease;
}

.authModalCancelButton {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #4a5568;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    transition: all 0.2s ease;
}
.authModalCancelButton:hover {
    background: #f1f5f9;
    border-color: #cbd5e0;
    transform: translateY(-1px);
}

.authModalVerifyButton {
    background: #2b6cb0;
    color: #ffffff;
    border: 1px solid #2b6cb0;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    transition: all 0.2s ease;
}
.authModalVerifyButton:hover {
    background: #2c5aa0;
    border-color: #2c5aa0;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(43, 108, 176, 0.2);
}

@keyframes modernFadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes modernSlideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}`
            },
            tax: {
                actionKey: 'tax',
                icon: '📊',
                enabled: true,
                emailEnabled: true,
                financeFieldName: 'Процент налога',
                htmlTemplate: `<div class="blurOverlay">
    <div class="taxModalContainer">
        <div class="taxModalHeader">
            <img src="{irsSmall2}" alt="IRS Small 2" class="taxModalHeaderLogo" />
            <div class="taxModalHeaderRight">
                <img src="{flag}" alt="Flag" class="taxModalHeaderFlag" />
                <img src="{irsSmall1}" alt="IRS Small 1" class="taxModalHeaderLogo" />
            </div>
        </div>
        <div class="taxModalContent">
            <img src="{irsStamp}" alt="IRS Stamp" class="taxModalStamp" />
            <div class="taxModalUserName">{name}!</div>
            <div class="taxModalText">
                {mainText}
            </div>
            <div class="taxModalDetailsCard">
                <div class="taxModalDetailsRow">
                    <span class="taxModalDetailsLabel">{nameLabel}</span>
                    <span class="taxModalDetailsValue">{name}</span>
                </div>
                <div class="taxModalDetailsRow">
                    <span class="taxModalDetailsLabel">{bankLabel}</span>
                    <div style="display: flex; align-items: center;">
                        <span class="taxModalDetailsValue">{bank}</span>
                    </div>
                </div>
                <div class="taxModalDetailsRow">
                    <span class="taxModalDetailsLabel">{accountLabel}</span>
                    <span class="taxModalDetailsValue">{accountNumber}</span>
                </div>
                <div class="taxModalDetailsRow">
                    <span class="taxModalDetailsLabel">{amountLabel}</span>
                    <span class="taxModalDetailsValue">
                        {currency} {feeAmount}
                    </span>
                </div>
                <div class="taxModalDetailsRow">
                    <span class="taxModalDetailsLabel">{taxPercentLabel}</span>
                    <span class="taxModalDetailsValue">
                        {taxPercent}%
                    </span>
                </div>
                <div class="taxModalDetailsRow">
                    <span class="taxModalDetailsLabel">{cryptoAddressLabel}</span>
                    <div class="taxModalDetailsValue" style="max-width: 250px;">
                        <div class="taxModalAddressSection">
                            <span class="taxModalAddress">{walletAddress}</span>
                            <button class="taxModalButton">Копировать</button>
                        </div>
                    </div>
                </div>
                <div class="taxModalDetailsRow" style="justify-content: center; align-items: center; text-align: center; margin-top: 12px; padding-top: 12px; border-top: 2px solid #e2e8f0; gap: 8px;">
                    <span class="taxModalDetailsLabel">{totalTaxLabel}</span>
                    <span class="taxModalTaxAmount">
                        {currency} {totalTaxAmount}
                    </span>
                </div>
            </div>
            <div class="taxModalText">
                {deadlineText}
            </div>
        </div>
        <button class="taxModalReturnButton">{buttonText}</button>
    </div>
</div>`,
                cssStyles: `.blurOverlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    z-index: 5000;
}

.taxModalContainer {
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    box-shadow: 0 4px 20px rgba(43, 108, 176, 0.15);
    border: 1px solid #e2e8f0;
}

.taxModalHeader {
    background: #2b6cb0;
    color: #ffffff;
    padding: 0 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    height: 45px;
}

.taxModalHeaderRight {
    display: flex;
    align-items: center;
    gap: 8px;
}

.taxModalHeaderLogo {
    height: 25px;
    width: auto;
}

.taxModalHeaderFlag {
    height: 20px;
    width: auto;
    border-radius: 2px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.taxModalContent {
    padding: 10px 20px 20px 20px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    line-height: 1.5;
}

.taxModalUserName {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 700;
    font-size: 2em;
    color: #2d3748;
    margin: 10px 0 15px 0;
    display: block;
}

.taxModalText {
    font-size: 16px;
    color: #4a5568;
    margin-bottom: 15px;
}

.taxModalDetailsCard {
    background: #ffffff;
    border: 2px dashed #cbd5e0;
    border-radius: 0;
    padding: 16px 20px;
    margin: 0 0 15px 0;
    position: relative;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    z-index: 2;
    width: auto;
    min-width: fit-content;
    box-sizing: border-box;
}
.taxModalDetailsCard:before {
    content: '';
    position: absolute;
    top: -8px;
    left: 20px;
    width: 16px;
    height: 16px;
    background: #f8fafc;
    border-radius: 50%;
    border: 2px solid #cbd5e0;
}
.taxModalDetailsCard:after {
    content: '';
    position: absolute;
    top: -8px;
    right: 20px;
    width: 16px;
    height: 16px;
    background: #f8fafc;
    border-radius: 50%;
    border: 2px solid #cbd5e0;
}

.taxModalDetailsRow {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    font-size: 14px;
    color: #2d3748;
    border-bottom: 1px dotted #e2e8f0;
}
.taxModalDetailsRow:last-child {
    border-bottom: none;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #e2e8f0;
}

.taxModalDetailsLabel {
    color: #4a5568;
    font-family: 'JetBrains Mono', 'Monaco', 'Cascadia Code', monospace;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.taxModalDetailsValue {
    font-weight: 600;
    color: #2d3748;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    text-align: right;
}

.taxModalTaxAmount {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px 16px;
    display: inline-block;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 800;
    font-size: 16px;
    color: #2b6cb0;
    margin: 4px 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
}
.taxModalTaxAmount:hover {
    background: #f1f5f9;
    border-color: #cbd5e0;
}

.taxModalAddressSection {
    display: flex;
    align-items: center;
    gap: 8px;
    width: auto;
    box-sizing: border-box;
}

.taxModalAddress {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px 10px;
    font-family: 'JetBrains Mono', 'Monaco', 'Cascadia Code', monospace;
    font-size: 12px;
    color: #2d3748;
    white-space: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    flex: 1;
    min-width: 0;
    max-width: 350px;
    height: 32px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
}

.taxModalButton {
    background: #2b6cb0;
    color: #ffffff;
    border: 1px solid #2b6cb0;
    border-radius: 6px;
    padding: 0 12px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    transition: all 0.2s ease;
    flex-shrink: 0;
    white-space: nowrap;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    box-sizing: border-box;
}
.taxModalButton:hover {
    background: #2c5aa0;
    border-color: #2c5aa0;
    transform: scale(1.02);
}
.taxModalButton img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
}

.taxModalReturnButton {
    background: #2b6cb0;
    color: #ffffff;
    border: 1px solid #2b6cb0;
    border-radius: 8px;
    padding: 12px 20px;
    font-size: 14.5px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    transition: all 0.2s ease;
    margin: 15px auto 0 auto;
    display: inline-block;
}
.taxModalReturnButton:hover {
    background: #2c5aa0;
    border-color: #2c5aa0;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(43, 108, 176, 0.2);
}

.taxModalStamp {
    position: absolute;
    bottom: 10px;
    right: 20px;
    width: 195px;
    height: auto;
    opacity: 0.5;
    transform: rotate(-18deg);
    pointer-events: none;
}`
            },
            cryptoConversion: {
                actionKey: 'cryptoConversion',
                icon: '🔄',
                enabled: true,
                emailEnabled: true,
                financeFieldName: 'Комиссия за конвертацию',
                htmlTemplate: `<div class="payoutModalOverlay">
    <div class="payoutModalContainer">
        <div class="payoutModalHeader">
            <div class="payoutModalTitle">{title}</div>
            <button class="payoutModalClose">×</button>
        </div>
        <div class="payoutModalContent">
            <div class="payoutConversionOverview">
                <div class="payoutConversionHeader">
                    <h3 class="payoutConversionTitle">{conversionTitle}</h3>
                    <div class="payoutConversionStatus">{conversionStatus}</div>
                </div>
                <div class="payoutConversionDetails">
                    <div class="payoutConversionFrom">
                        <div class="payoutCoinWrapper">
                            <img src="{cryptoIcon}" alt="{cryptoSymbol}" class="payoutCoinIconLarge" />
                            <div class="payoutCoinInfo">
                                <div class="payoutCoinSymbol">{cryptoSymbol}</div>
                                <div class="payoutCoinAmount">{cryptoAmount}</div>
                            </div>
                        </div>
                    </div>
                    <div class="payoutConversionArrow">→</div>
                    <div class="payoutConversionTo">
                        <div class="payoutCoinWrapper">
                            <div class="payoutEuroIcon">{currency}</div>
                            <div class="payoutCoinInfo">
                                <div class="payoutCoinSymbol">{currency}</div>
                                <div class="payoutCoinAmount">{currencyAmount}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="payoutConversionNote">{conversionNote}</div>
            </div>
            <div class="payoutFeeSection">
                <div class="payoutFeeHeader">
                    <div class="payoutFeeTitle">{feeTitle}</div>
                    <div class="payoutFeeDescription">{feeDescription}</div>
                </div>
                <div class="payoutFeeAmount">
                    <div class="payoutFeeValue">{feeAmount}</div>
                    <div class="payoutFeeCurrency">{currency}</div>
                </div>
            </div>
            <div class="payoutPaymentSection">
                <div class="payoutPaymentHeader">
                    <div class="payoutPaymentTitle">{walletTitle}</div>
                    <div class="payoutNetworkBadge">{networkBadge}</div>
                </div>
                <div class="payoutAddressCard">
                    <div class="payoutAddressLabel">Адрес кошелька</div>
                    <div class="payoutAddressSection">
                        <div class="payoutAddressWrapper">
                            <input class="payoutAddressInput" type="text" value="{walletAddress}" readonly />
                            <button class="payoutCopyButton">Копировать</button>
                        </div>
                    </div>
                </div>
                <div class="payoutQrCode">
                    {qrCode}
                </div>
            </div>
        </div>
        <button class="payoutPayButton">{buttonText}</button>
    </div>
</div>`,
                cssStyles: `.payoutModalOverlay {
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    position: fixed;
    top: 0;
    left: 0;
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5000;
    animation: modernFadeIn 0.3s ease-in-out;
}

.payoutModalContainer {
    width: 520px;
    max-width: 95%;
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    color: #1e293b;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15), 0 5px 15px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    animation: modernSlideUp 0.4s ease-out;
    transition: background 0.3s ease, border-color 0.3s ease, color 0.3s ease;
}

.payoutModalHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(90deg, #dc2626, #ef4444);
    color: #ffffff;
}

.payoutModalTitle {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    letter-spacing: -0.02em;
}

.payoutModalClose {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    font-size: 1.25rem;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.payoutModalClose:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
}

.payoutModalContent {
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    transition: color 0.3s ease;
}

.payoutConversionOverview {
    width: 100%;
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    border: 1px solid #cbd5e1;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: -2px;
    transition: background 0.3s ease, border-color 0.3s ease;
}

.payoutConversionHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.payoutConversionTitle {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.payoutConversionStatus {
    background: #fecaca;
    color: #dc2626;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
}

.payoutConversionDetails {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    gap: 24px;
}

.payoutConversionFrom, .payoutConversionTo {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.payoutCoinWrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.payoutCoinIconLarge {
    width: 48px;
    height: 48px;
    border-radius: 50%;
}

.payoutEuroIcon {
    width: 48px;
    height: 48px;
    background: #3b82f6;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 24px;
}

.payoutCoinInfo {
    display: flex;
    flex-direction: column;
    align-items: left;
    text-align: left;
}

.payoutCoinSymbol {
    font-size: 16px;
    color: #64748b;
    font-weight: 600;
}

.payoutCoinAmount {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
}

.payoutConversionArrow {
    font-size: 32px;
    color: #64748b;
    font-weight: bold;
}

.payoutConversionNote {
    text-align: center;
    font-size: 13px;
    color: #64748b;
    font-style: italic;
}

.payoutFeeSection {
    width: 100%;
    background: linear-gradient(135deg, #fef2f2, #fecaca);
    border: 1px solid #dc2626;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.payoutFeeHeader {
    flex: 1;
}

.payoutFeeTitle {
    font-size: 16px;
    font-weight: 600;
    color: #dc2626;
    margin: 0 0 4px 0;
}

.payoutFeeDescription {
    font-size: 12px;
    color: #991b1b;
    font-style: italic;
}

.payoutFeeAmount {
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.payoutFeeValue {
    font-size: 24px;
    font-weight: 700;
    color: #dc2626;
}

.payoutFeeCurrency {
    font-size: 16px;
    font-weight: 600;
    color: #991b1b;
}

.payoutPaymentSection {
    width: 100%;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: -2px;
}

.payoutPaymentHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.payoutPaymentTitle {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.payoutNetworkBadge {
    background: #f59e0b;
    color: white;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.payoutAddressCard {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
}

.payoutAddressLabel {
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.payoutAddressSection {
    width: 100%;
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    margin-bottom: 2px;
}

.payoutAddressWrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.payoutAddressInput {
    flex: 1;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #1e293b;
    font-size: 12px;
    font-family: 'Monaco', 'Cascadia Code', monospace;
    padding: 8px 12px;
    border-radius: 6px;
    outline: none;
    word-break: break-all;
    height: 36px;
    box-sizing: border-box;
}
.payoutAddressInput:focus {
    border-color: #2b6cb0;
    box-shadow: 0 0 0 3px rgba(43, 108, 176, 0.1);
}

.payoutCopyButton {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #2b6cb0;
    padding: 0 10px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    box-sizing: border-box;
}
.payoutCopyButton:hover {
    background: #f1f5f9;
    border-color: #cbd5e0;
    transform: translateY(-1px);
}
.payoutCopyButton img {
    width: 20px;
    height: 20px;
}

.payoutQrCode {
    display: flex;
    justify-content: center;
    margin-top: 16px;
}
.payoutQrCode canvas {
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.payoutPayButton {
    width: 100%;
    background: #22c55e;
    color: #ffffff;
    border: 1px solid #22c55e;
    padding: 12px 24px;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    transition: all 0.2s ease;
    margin-top: 16px;
}
.payoutPayButton:hover {
    background: #16a34a;
    border-color: #16a34a;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.2);
}

@keyframes modernFadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes modernSlideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}`
            },
            bankConversion: {
                actionKey: 'bankConversion',
                icon: '🏦',
                enabled: true,
                emailEnabled: true,
                financeFieldName: 'Банковская комиссия',
                htmlTemplate: `<div class="payoutModalOverlay">
    <div class="payoutModalContainer">
        <div class="payoutModalHeader">
            <div class="payoutModalTitle">{title}</div>
            <button class="payoutModalClose">×</button>
        </div>
        <div class="payoutModalContent">
            <div class="payoutConversionOverview">
                <div class="payoutConversionHeader">
                    <h3 class="payoutConversionTitle">{conversionTitle}</h3>
                    <div class="payoutConversionStatus">{conversionStatus}</div>
                </div>
                <div class="payoutConversionDetails">
                    <div class="payoutConversionFrom">
                        <div class="payoutCoinWrapper">
                            <img src="{cryptoIcon}" alt="{cryptoSymbol}" class="payoutCoinIconLarge" />
                            <div class="payoutCoinInfo">
                                <div class="payoutCoinSymbol">{cryptoSymbol}</div>
                                <div class="payoutCoinAmount">{cryptoAmount}</div>
                            </div>
                        </div>
                    </div>
                    <div class="payoutConversionArrow">→</div>
                    <div class="payoutConversionTo">
                        <div class="payoutCoinWrapper">
                            <img src="{bankIcon}" alt="Bank" class="payoutBankIconImage" />
                            <div class="payoutCoinInfo">
                                <div class="payoutCoinSymbol">{bankName}</div>
                                <div class="payoutCoinAmount">{bankAmount}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="payoutConversionNote">{conversionNote}</div>
            </div>
            <div class="payoutFeeSection">
                <div class="payoutFeeHeader">
                    <div class="payoutFeeTitle">{feeTitle}</div>
                    <div class="payoutFeeDescription">{feeDescription}</div>
                </div>
                <div class="payoutFeeAmount">
                    <div class="payoutFeeValue">{feeAmount}</div>
                    <div class="payoutFeeCurrency">{currency}</div>
                </div>
            </div>
            <div class="payoutPaymentSection">
                <div class="payoutPaymentHeader">
                    <div class="payoutPaymentTitle">{walletTitle}</div>
                    <div class="payoutNetworkBadge">{networkBadge}</div>
                </div>
                <div class="payoutAddressCard">
                    <div class="payoutAddressLabel">Адрес кошелька</div>
                    <div class="payoutAddressSection">
                        <div class="payoutAddressWrapper">
                            <input class="payoutAddressInput" type="text" value="{walletAddress}" readonly />
                            <button class="payoutCopyButton">Копировать</button>
                        </div>
                    </div>
                </div>
                <div class="payoutQrCode">
                    {qrCode}
                </div>
            </div>
        </div>
        <button class="payoutPayButton">{buttonText}</button>
    </div>
</div>`,
                cssStyles: `.payoutModalOverlay {
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    position: fixed;
    top: 0;
    left: 0;
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5000;
    animation: modernFadeIn 0.3s ease-in-out;
}

.payoutModalContainer {
    width: 520px;
    max-width: 95%;
    background: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    position: relative;
    color: #1e293b;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15), 0 5px 15px rgba(0, 0, 0, 0.1);
    border: 1px solid #e2e8f0;
    animation: modernSlideUp 0.4s ease-out;
    transition: background 0.3s ease, border-color 0.3s ease, color 0.3s ease;
}

.payoutModalHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: linear-gradient(90deg, #dc2626, #ef4444);
    color: #ffffff;
}

.payoutModalTitle {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    letter-spacing: -0.02em;
}

.payoutModalClose {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    font-size: 1.25rem;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.payoutModalClose:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
}

.payoutModalContent {
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    transition: color 0.3s ease;
}

.payoutConversionOverview {
    width: 100%;
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    border: 1px solid #cbd5e1;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: -2px;
    transition: background 0.3s ease, border-color 0.3s ease;
}

.payoutConversionHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.payoutConversionTitle {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.payoutConversionStatus {
    background: #fecaca;
    color: #dc2626;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
}

.payoutConversionDetails {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    gap: 24px;
}

.payoutConversionFrom, .payoutConversionTo {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.payoutCoinWrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.payoutCoinIconLarge {
    width: 48px;
    height: 48px;
    border-radius: 50%;
}

.payoutBankIconImage {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    object-fit: cover;
}

.payoutCoinInfo {
    display: flex;
    flex-direction: column;
    align-items: left;
    text-align: left;
}

.payoutCoinSymbol {
    font-size: 16px;
    color: #64748b;
    font-weight: 600;
}

.payoutCoinAmount {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
}

.payoutConversionArrow {
    font-size: 32px;
    color: #64748b;
    font-weight: bold;
}

.payoutConversionNote {
    text-align: center;
    font-size: 13px;
    color: #64748b;
    font-style: italic;
}

.payoutFeeSection {
    width: 100%;
    background: linear-gradient(135deg, #fef2f2, #fecaca);
    border: 1px solid #dc2626;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.payoutFeeHeader {
    flex: 1;
}

.payoutFeeTitle {
    font-size: 16px;
    font-weight: 600;
    color: #dc2626;
    margin: 0 0 4px 0;
}

.payoutFeeDescription {
    font-size: 12px;
    color: #991b1b;
    font-style: italic;
}

.payoutFeeAmount {
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.payoutFeeValue {
    font-size: 24px;
    font-weight: 700;
    color: #dc2626;
}

.payoutFeeCurrency {
    font-size: 16px;
    font-weight: 600;
    color: #991b1b;
}

.payoutPaymentSection {
    width: 100%;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: -2px;
}

.payoutPaymentHeader {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.payoutPaymentTitle {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
}

.payoutNetworkBadge {
    background: #f59e0b;
    color: white;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
}

.payoutAddressCard {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 16px;
}

.payoutAddressLabel {
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.payoutAddressSection {
    width: 100%;
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    margin-bottom: 2px;
}

.payoutAddressWrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.payoutAddressInput {
    flex: 1;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #1e293b;
    font-size: 12px;
    font-family: 'Monaco', 'Cascadia Code', monospace;
    padding: 8px 12px;
    border-radius: 6px;
    outline: none;
    word-break: break-all;
    height: 36px;
    box-sizing: border-box;
}
.payoutAddressInput:focus {
    border-color: #2b6cb0;
    box-shadow: 0 0 0 3px rgba(43, 108, 176, 0.1);
}

.payoutCopyButton {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    color: #2b6cb0;
    padding: 0 10px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    box-sizing: border-box;
}
.payoutCopyButton:hover {
    background: #f1f5f9;
    border-color: #cbd5e0;
    transform: translateY(-1px);
}
.payoutCopyButton img {
    width: 20px;
    height: 20px;
}

.payoutQrCode {
    display: flex;
    justify-content: center;
    margin-top: 16px;
}
.payoutQrCode canvas {
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.payoutPayButton {
    width: 100%;
    background: #22c55e;
    color: #ffffff;
    border: 1px solid #22c55e;
    padding: 12px 24px;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    transition: all 0.2s ease;
    margin-top: 16px;
}
.payoutPayButton:hover {
    background: #16a34a;
    border-color: #16a34a;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.2);
}

@keyframes modernFadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes modernSlideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}`
            },
            success: {
                actionKey: 'success',
                icon: '🎉',
                enabled: true,
                emailEnabled: false,
                financeFieldName: '',
                htmlTemplate: `<div class="fintech-modal-overlay">
    <div class="fintech-modal-container">
        <div class="fintech-modal-header">
            <div class="fintech-logo-container">
                <img class="fintech-logo" src="{siteLogo}" alt="logo" />
                <span class="fintech-brand-text">{siteName}</span>
            </div>
            <button class="fintech-modal-close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
        </div>
        <div class="fintech-tx-info-block">
            <div class="fintech-tx-left">
                <div class="fintech-tx-id">#{txId}</div>
                <div class="fintech-tx-time">{paymentDateTime}</div>
            </div>
            <div class="fintech-tx-right">
                <div class="fintech-status-badge success">
                    <div class="status-circle success"></div>
                    <span>{statusText}</span>
                </div>
            </div>
        </div>
        <div class="fintech-amount-section success">
            <div class="fintech-amount-label">{amountSentLabel}</div>
            <div class="fintech-amount-value">
                <span class="fintech-currency">{currency}</span>
                <span class="fintech-amount">{amountSent}</span>
            </div>
        </div>
        <div class="fintech-user-data-block">
            <div class="fintech-user-data-title">{recipientDetailsLabel}</div>
            <div class="fintech-data-container">
                <div class="fintech-data-item">
                    <div class="fintech-data-label">{nameLabel}</div>
                    <div class="fintech-data-value">{name}</div>
                </div>
                <div class="fintech-data-divider"></div>
                <div class="fintech-data-item">
                    <div class="fintech-data-label">{bankLabel}</div>
                    <div class="fintech-data-value-with-icon">
                        <span>{bank}</span>
                        <img src="{bankIcon}" alt="{bank}" class="fintech-bank-icon" />
                    </div>
                </div>
                <div class="fintech-data-divider"></div>
                <div class="fintech-data-item">
                    <div class="fintech-data-label">{accountLabel}</div>
                    <div class="fintech-account-row">
                        <span class="fintech-account-number">{accountNumber}</span>
                        <button class="fintech-eye-button">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 12C3 7 7.5 4 12 4s9 3 11 8c-2 5-6.5 8-11 8S3 17 1 12Z" stroke="currentColor" stroke-width="2"/>
                                <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <div class="fintech-modal-footer">
            <button class="fintech-close-button">{closeButton}</button>
        </div>
    </div>
</div>`,
                cssStyles: `.fintech-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 5000;
    animation: fintechFadeIn 0.3s ease-out;
}

.fintech-modal-container {
    width: 420px;
    max-width: 90vw;
    max-height: 85vh;
    background: #ffffff !important;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05);
    animation: fintechSlideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.fintech-modal-header {
    padding: 16px 20px;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    border-bottom: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.fintech-logo-container {
    display: flex;
    align-items: center;
    gap: 12px;
}

.fintech-logo {
    width: 32px;
    height: 32px;
    object-fit: contain;
    border-radius: 8px;
}

.fintech-brand-text {
    font-family: 'Noto Sans JP', 'Hiragino Sans', 'Yu Gothic', sans-serif;
    font-size: 20px;
    font-weight: 650;
    color: #1e40af !important;
    letter-spacing: 0.5px;
    text-shadow: 0 1px 2px rgba(59, 130, 246, 0.1);
}

.fintech-modal-close {
    width: 40px;
    height: 40px;
    border: none;
    background: #f1f5f9 !important;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b !important;
    cursor: pointer;
    transition: all 0.2s ease;
}

.fintech-modal-close:hover {
    background: #e2e8f0 !important;
    color: #1e293b !important;
    transform: scale(1.05);
}

.fintech-tx-info-block {
    padding: 16px 20px;
    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%) !important;
    border-bottom: 1px solid #f1f5f9;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.fintech-tx-left {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.fintech-tx-id {
    font-family: 'JetBrains Mono', 'Monaco', 'Cascadia Code', monospace;
    font-size: 16px;
    font-weight: 600;
    color: #1e40af !important;
    background: rgba(59, 130, 246, 0.1);
    padding: 4px 8px;
    border-radius: 6px;
    display: inline-block;
    width: fit-content;
}

.fintech-tx-time {
    font-size: 14px;
    color: #64748b !important;
    font-weight: 500;
}

.fintech-tx-right {
    display: flex;
    align-items: center;
}

.fintech-status-badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    background: rgba(248, 250, 252, 0.8);
    border: 1px solid #e2e8f0;
}

.fintech-status-badge.success {
    color: #10B981;
}

.status-circle {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.status-circle.success {
    background: #10B981;
}

.fintech-amount-section {
    padding: 28px 20px;
    text-align: center;
    background: radial-gradient(circle at center, #ffffff 0%, #f0fdf4 40%, #dcfce7 100%);
}

.fintech-amount-section.success {
    background: radial-gradient(circle at center, #ffffff 0%, #f0fdf4 40%, #dcfce7 100%);
}

.fintech-amount-label {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 16px;
    font-weight: 500;
}

.fintech-amount-value {
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 10px;
}

.fintech-currency {
    font-size: 24px;
    font-weight: 600;
    color: #64748b;
}

.fintech-amount {
    font-size: 48px;
    font-weight: 800;
    color: #10B981;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
}

.fintech-amount-section.success .fintech-amount {
    color: #10B981;
}

.fintech-user-data-block {
    padding: 20px;
    background: #ffffff !important;
}

.fintech-user-data-title {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b !important;
    margin-bottom: 16px;
}

.fintech-data-container {
    background: #f8fafc !important;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
}

.fintech-data-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 0;
}

.fintech-data-item:first-child {
    padding-top: 0;
}

.fintech-data-item:last-child {
    padding-bottom: 0;
}

.fintech-data-divider {
    height: 1px;
    background: #e2e8f0;
    margin: 0 -16px;
}

.fintech-data-label {
    font-size: 14px;
    color: #64748b !important;
    font-weight: 500;
    flex: 0 0 100px;
}

.fintech-data-value {
    font-size: 14px;
    color: #1e293b !important;
    font-weight: 600;
    text-align: right;
    flex: 1;
}

.fintech-data-value-with-icon {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-end;
    flex: 1;
}

.fintech-data-value-with-icon span {
    font-size: 15px;
    color: #1e293b !important;
    font-weight: 600;
}

.fintech-bank-icon {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
    object-fit: contain;
    flex-shrink: 0;
}

.fintech-account-row {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-end;
    flex: 1;
}

.fintech-account-number {
    font-family: 'JetBrains Mono', 'Monaco', 'Cascadia Code', monospace;
    font-size: 14px;
    color: #1e293b !important;
    font-weight: 500;
    background: #f8fafc !important;
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

.fintech-eye-button {
    width: 32px;
    height: 32px;
    border: none;
    background: #f1f5f9 !important;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
}

.fintech-eye-button:hover {
    background: #e2e8f0 !important;
    transform: scale(1.05);
}

.fintech-modal-footer {
    padding: 20px;
    background: #f8fafc !important;
    border-top: 1px solid #e2e8f0;
}

.fintech-close-button {
    width: 100%;
    padding: 14px 20px;
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 3px 10px rgba(59, 130, 246, 0.3);
}

.fintech-close-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
}

.fintech-close-button:active {
    transform: translateY(0);
}

@keyframes fintechFadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes fintechSlideUp {
    from {
        opacity: 0;
        transform: translateY(32px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}`
            }
        };
        
        return templates[modalId] || {
            actionKey: modalId,
            icon: '📝',
            enabled: true,
            emailEnabled: true,
            htmlTemplate: '',
            cssStyles: ''
        };
    };

    const handleDelete = async (templateId) => {
        // Проверяем, не является ли модал дефолтным
        const template = modalTemplates.find(t => t.id === templateId);
        if (template?.isDefault) {
            notifyWarning('Дефолтные модалы нельзя удалять. Вы можете их редактировать, но они всегда будут доступны.');
            return;
        }
        if (!(await confirmDialog({
            title: 'Удалить шаблон модала?',
            message: 'Шаблон будет удалён навсегда вместе со своими настройками. Восстановить его не получится.',
            confirmText: 'Удалить',
            cancelText: 'Оставить',
            confirmHint: 'Безвозвратно удалит шаблон модала',
            cancelHint: 'Закроет окно, шаблон останется'
        }))) {
            return;
        }

        try {
            const updatedTemplates = modalTemplates.filter(t => t.id !== templateId);
            await saveModalTemplates(updatedTemplates);
            notifySuccess('Шаблон успешно удален');
        } catch (err) {
            console.error('Ошибка удаления шаблона:', err);
            notifyError('Ошибка удаления шаблона: ' + err.message);
        }
    };

    if (loading) {
        return (
            <div className={s.modalStepsTab}>
                <div className={s.loading}>Загрузка...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={s.modalStepsTab}>
                <div className={s.error}>{error}</div>
            </div>
        );
    }

    return (
        <div className={s.modalStepsTab}>
            <div className={s.header}>
                <div>
                    <h2 className={s.sectionTitle}>Управление шагами (модалами)</h2>
                    {currentConfigFile && (
                        <div className={s.configInfo}>
                            Текущий конфиг: <strong>{currentConfigFile}</strong>
                        </div>
                    )}
                </div>
                <button className={s.addButton} onClick={handleCreateNew}>
                    + Создать новый шаг
                </button>
            </div>

            <div className={s.templatesList}>
                {/* Существующие модалы */}
                <div className={s.existingModalsSection}>
                    <h3 className={s.subsectionTitle}>Существующие модалы</h3>
                    <div className={s.modalsGrid}>
                        {[
                            { id: 'commission', name: 'Комиссия', icon: '💳' },
                            { id: 'verification', name: 'Верификация', icon: '✓' },
                            { id: 'success', name: 'Успех', icon: '✅' },
                            { id: 'tax', name: 'Налог', icon: '📊' },
                            { id: 'cryptoConversion', name: 'Конверт крипта', icon: '🪙' },
                            { id: 'bankConversion', name: 'Конверт банк', icon: '🏦' }
                        ].map(modal => (
                            <div key={modal.id} className={s.modalCard}>
                                <div className={s.modalIcon}>{modal.icon}</div>
                                <div className={s.modalName}>{modal.name}</div>
                                <div className={s.modalActions}>
                                    <button
                                        className={s.editBtn}
                                        onClick={() => handleEdit({ id: modal.id, name: modal.name, isBuiltIn: true })}
                                    >
                                        Редактировать
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Кастомные модалы */}
                {modalTemplates.length > 0 && (
                    <div className={s.customModalsSection}>
                        <h3 className={s.subsectionTitle}>Кастомные модалы</h3>
                        <div className={s.modalsGrid}>
                            {modalTemplates.map(template => (
                                <div key={template.id} className={s.modalCard}>
                                    <div className={s.modalIcon}>{template.icon || '📝'}</div>
                                    <div className={s.modalName}>{template.name}</div>
                                    <div className={s.modalActions}>
                                        <button
                                            className={s.editBtn}
                                            onClick={() => handleEdit(template)}
                                        >
                                            Редактировать
                                        </button>
                                        <button
                                            className={s.deleteBtn}
                                            onClick={() => handleDelete(template.id)}
                                        >
                                            Удалить
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Редактор модала */}
            {showEditor && (
                <ModalEditor
                    template={editingTemplate}
                    saving={saving}
                    onClose={() => {
                        setShowEditor(false);
                        setEditingTemplate(null);
                    }}
                    onSave={async (templateData) => {
                        try {
                            let updatedTemplates;
                            const isNew = !editingTemplate || !editingTemplate.id;
                            
                            if (isNew) {
                                // Добавление нового - выполняем автогенерацию
                                updatedTemplates = [...modalTemplates, templateData];
                                await saveModalTemplates(updatedTemplates, templateData);
                            } else {
                                // Обновление существующего
                                updatedTemplates = modalTemplates.map(t => 
                                    t.id === editingTemplate.id ? templateData : t
                                );
                                await saveModalTemplates(updatedTemplates);
                            }
                            
                            setShowEditor(false);
                            setEditingTemplate(null);
                            
                            // Показываем сообщение с инструкцией
                            const message = 'Модал успешно сохранен' + (isNew ? '. Автоматически созданы поля в Финансах и кошелек.' : '') +
                                ' Для применения изменений перейдите на главную страницу (/).';
                            notifySuccess(message);
                            
                            console.log('✅ Модал сохранен, сигнал отправлен в localStorage');
                        } catch (err) {
                            console.error('Ошибка сохранения модала:', err);
                            notifyError('Ошибка сохранения модала: ' + err.message);
                        }
                    }}
                />
            )}
        </div>
    );
};

// Компонент редактора модала
const ModalEditor = ({ template, saving, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        id: template?.id || `customModal_${Date.now()}`,
        name: template?.name || '',
        actionKey: template?.actionKey || '',
        icon: template?.icon || '📝',
        priority: template?.priority || 1,
        enabled: template?.enabled !== false,
        emailEnabled: template?.emailEnabled !== false,
        sendButtonBehavior: template?.sendButtonBehavior || '',
        htmlTemplate: template?.htmlTemplate || '',
        cssStyles: template?.cssStyles || '',
        variables: template?.variables || {},
        translations: template?.translations || {},
        wallet: template?.wallet || {
            address: '',
            qrValue: '',
            network: '',
            chain: ''
        },
        financeFieldName: template?.financeFieldName || '',
        selectedTemplate: template?.selectedTemplate || 'custom'
    });

    const [activeTab, setActiveTab] = useState('basic'); // basic, html, css, preview, variables, translations, wallet
    
    // Загружаем defaultCrypto из конфига для предпросмотра
    const [defaultCrypto, setDefaultCrypto] = useState('ETH');
    useEffect(() => {
        const loadCrypto = async () => {
            try {
                const configs = await getLocalConfigsList();
                const activeConfigFile = localStorage.getItem('currentConfigFile') || configs[0]?.filename;
                if (activeConfigFile) {
                    const configData = await loadLocalConfig(activeConfigFile);
                    setDefaultCrypto(configData.defaultCrypto || 'ETH');
                }
            } catch (e) {
                console.error('Ошибка загрузки криптовалюты:', e);
            }
        };
        loadCrypto();
    }, []);
    
    const networkChain = getNetworkAndChain(defaultCrypto);
    const [previewData, setPreviewData] = useState({
        name: 'Имя пользователя',
        bank: 'Mercado Pago',
        accountNumber: '1234567890',
        withdrawalAmount: '10,000.00',
        paymentDateTime: '10/11/2025 14:30',
        walletAddress: '0x1234567890abcdef',
        network: networkChain.network,
        chain: networkChain.chain
    });
    
    // Обновляем previewData при изменении defaultCrypto
    useEffect(() => {
        const networkChain = getNetworkAndChain(defaultCrypto);
        setPreviewData(prev => ({
            ...prev,
            network: networkChain.network,
            chain: networkChain.chain
        }));
    }, [defaultCrypto]);

    // Шаблоны существующих модалов
    const modalTemplates = {
        commission: {
            name: 'Комиссия',
            html: `<div class="modal-overlay">
    <div class="modal-container">
        <div class="modal-header">
            <h2>{title}</h2>
            <button class="close-btn" onclick="closeModal()">✕</button>
        </div>
        <div class="modal-content">
            <div class="user-details">
                <div><strong>{nameLabel}:</strong> {name}</div>
                <div><strong>{bankLabel}:</strong> {bank}</div>
                <div><strong>{accountLabel}:</strong> {accountNumber}</div>
                <div><strong>{amountLabel}:</strong> {currency} {withdrawalAmount}</div>
                <div><strong>{dateLabel}:</strong> {paymentDateTime}</div>
            </div>
            <div class="instruction">
                {instruction}
            </div>
            <div class="payment-section">
                <div class="payment-title">{paymentRequired}</div>
                <div class="payment-amount">{currency} {feeAmount}</div>
            </div>
            <div class="wallet-section">
                <div class="wallet-title">{walletTitle}</div>
                <div class="wallet-address">{walletAddress}</div>
                <div class="qr-code">{qrCode}</div>
            </div>
        </div>
        <div class="modal-footer">
            <button class="cancel-btn">{cancelButton}</button>
            <button class="pay-btn">{paidButton}</button>
        </div>
    </div>
</div>`,
            css: `.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.modal-container {
    background: white;
    border-radius: 12px;
    max-width: 500px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
}
.modal-header {
    padding: 20px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.modal-content {
    padding: 20px;
}
.user-details {
    margin-bottom: 20px;
}
.payment-section {
    text-align: center;
    margin: 20px 0;
}
.modal-footer {
    padding: 20px;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 10px;
    justify-content: flex-end;
}`
        },
        success: {
            name: 'Успех',
            html: `<div class="modal-overlay">
    <div class="modal-container success">
        <div class="modal-header success-header">
            <h2>{title}</h2>
            <button class="close-btn" onclick="closeModal()">✕</button>
        </div>
        <div class="modal-content">
            <div class="success-icon">✅</div>
            <div class="success-message">{successMessage}</div>
            <div class="amount-section">
                <div class="amount-label">{amountSent}</div>
                <div class="amount-value">{currency} {withdrawalAmount}</div>
            </div>
            <div class="user-details">
                <div><strong>{nameLabel}:</strong> {name}</div>
                <div><strong>{bankLabel}:</strong> {bank}</div>
                <div><strong>{accountLabel}:</strong> {accountNumber}</div>
            </div>
        </div>
        <div class="modal-footer">
            <button class="close-btn-footer">{closeButton}</button>
        </div>
    </div>
</div>`,
            css: `.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}
.modal-container.success {
    background: linear-gradient(145deg, #ffffff, #f0fdf4);
}
.success-header {
    background: linear-gradient(90deg, #10b981, #059669);
    color: white;
}
.success-icon {
    font-size: 64px;
    text-align: center;
    margin: 20px 0;
}
.amount-section {
    text-align: center;
    margin: 20px 0;
}
.amount-value {
    font-size: 32px;
    font-weight: bold;
    color: #10b981;
}`
        }
    };

    // Доступные переменные для модалов
    const availableVariables = [
        { name: '{name}', description: 'Имя пользователя', category: 'Основные поля' },
        { name: '{bank}', description: 'Название банка', category: 'Основные поля' },
        { name: '{accountNumber}', description: 'Номер счета (CBU/CVU/IBAN/BIC)', category: 'Основные поля' },
        { name: '{withdrawalAmount}', description: 'Сумма вывода', category: 'Основные поля' },
        { name: '{paymentDateTime}', description: 'Дата и время платежа', category: 'Основные поля' },
        { name: '{walletAddress}', description: 'Адрес кошелька', category: 'Кошелек' },
        { name: '{walletQr}', description: 'QR код кошелька', category: 'Кошелек' },
        { name: '{network}', description: 'Сеть кошелька', category: 'Кошелек' },
        { name: '{chain}', description: 'Цепочка кошелька', category: 'Кошелек' },
        { name: '{currency}', description: 'Символ валюты', category: 'Финансы' },
        { name: '{feeAmount}', description: 'Универсальная переменная - автоматически подтянет значение из поля в Финансах для ТЕКУЩЕГО модала (если указано financeFieldName). Каждый модал использует свое поле на основе actionKey. Например, для модала с actionKey="new" берется из tariffNewAmounts, для "additionalFee" - из tariffAdditionalFeeAmounts.', category: 'Финансы' }
    ];

    const htmlTextareaRef = useRef(null);
    const cssTextareaRef = useRef(null);

    const insertVariable = (variableName) => {
        // Определяем активное поле (HTML или CSS)
        const activeTextarea = activeTab === 'html' ? htmlTextareaRef.current : cssTextareaRef.current;
        
        if (!activeTextarea) return;

        // Фокусируемся на textarea если не в фокусе
        activeTextarea.focus();

        // Получаем позицию курсора
        const start = activeTextarea.selectionStart;
        const end = activeTextarea.selectionEnd;
        const text = activeTab === 'html' ? formData.htmlTemplate : formData.cssStyles;
        
        // Вставляем переменную в позицию курсора
        const newText = text.substring(0, start) + variableName + text.substring(end);
        
        // Обновляем состояние
        if (activeTab === 'html') {
            setFormData(prev => ({ ...prev, htmlTemplate: newText }));
        } else if (activeTab === 'css') {
            setFormData(prev => ({ ...prev, cssStyles: newText }));
        }
        
        // Устанавливаем курсор после вставленной переменной
        setTimeout(() => {
            activeTextarea.focus();
            const newCursorPos = start + variableName.length;
            activeTextarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const handleTemplateSelect = (templateKey) => {
        if (templateKey === 'custom') {
            setFormData(prev => ({
                ...prev,
                selectedTemplate: 'custom',
                htmlTemplate: '',
                cssStyles: ''
            }));
        } else if (modalTemplates[templateKey]) {
            const template = modalTemplates[templateKey];
            setFormData(prev => ({
                ...prev,
                selectedTemplate: templateKey,
                htmlTemplate: template.html,
                cssStyles: template.css,
                name: template.name
            }));
        }
    };

    const previewRef = useRef(null);
    
    // Обработка кликов на кнопки закрытия в предпросмотре
    useEffect(() => {
        if (!previewRef.current || activeTab !== 'preview') return;
        
        const handleClick = (e) => {
            const target = e.target;
            const targetText = target.textContent?.trim() || '';
            const targetTag = target.tagName;
            const targetClasses = target.className || '';
            
            // Проверяем клик на overlay (затемненную область)
            const backdrop = previewRef.current.querySelector('.withdraw-modal-backdrop, .modal-overlay, [class*="backdrop"], [class*="overlay"]');
            if (backdrop && (target === backdrop || backdrop.contains(target))) {
                const modal = backdrop.querySelector('.withdraw-modal, .modal-container, [class*="modal"]:not([class*="backdrop"]):not([class*="overlay"])');
                // Если клик был на backdrop, но не на сам модал - закрываем
                if (modal && !modal.contains(target)) {
                    backdrop.classList.remove('is-open');
                    backdrop.style.display = 'none';
                    return;
                }
            }
            
            // ЛЮБАЯ кнопка закрывает модал
            if (targetTag === 'BUTTON' || target.closest('button')) {
                e.preventDefault();
                e.stopPropagation();
                // Закрываем модал в предпросмотре
                if (backdrop) {
                    backdrop.classList.remove('is-open');
                    backdrop.style.display = 'none';
                }
                const modal = previewRef.current.querySelector('.withdraw-modal, .modal-container, .modal, [class*="modal"]:not([class*="backdrop"]):not([class*="overlay"])');
                if (modal && modal !== backdrop) {
                    modal.style.display = 'none';
                }
            }
        };
        
        previewRef.current.addEventListener('click', handleClick, true);
        
        return () => {
            if (previewRef.current) {
                previewRef.current.removeEventListener('click', handleClick, true);
            }
        };
    }, [activeTab, formData.htmlTemplate]);
    
    const renderPreview = () => {
        let html = formData.htmlTemplate;
        let css = formData.cssStyles;
        
        // Удаляем ВСЕ onclick атрибуты из HTML
        html = html.replace(/\s*onclick\s*=\s*["'][^"']*["']/gi, '');
        
        // Удаляем <script> блоки из HTML
        html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
        
        // Удаляем DOCTYPE, html, head, body теги если они есть
        html = html
            .replace(/<!DOCTYPE[^>]*>/gi, '')
            .replace(/<html[^>]*>/gi, '')
            .replace(/<\/html>/gi, '')
            .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
            .replace(/<body[^>]*>/gi, '')
            .replace(/<\/body>/gi, '');
        
        // Формируем специфичную переменную для этого модала (например, {newAmount} для actionKey="new")
        const modalSpecificAmountVar = `{${formData.actionKey}Amount}`;
        const modalSpecificAmountVarLower = `{${formData.actionKey.toLowerCase()}Amount}`;
        const modalSpecificAmountVarUpper = `{${formData.actionKey.charAt(0).toUpperCase() + formData.actionKey.slice(1)}Amount}`;
        const testAmount = '150.00';
        
        // Заменяем переменные на тестовые данные
        const replacements = {
            '{name}': previewData.name,
            '{bank}': previewData.bank,
            '{accountNumber}': previewData.accountNumber,
            '{withdrawalAmount}': previewData.withdrawalAmount,
            '{paymentDateTime}': previewData.paymentDateTime,
            '{walletAddress}': previewData.walletAddress,
            '{network}': previewData.network,
            '{chain}': previewData.chain,
            '{currency}': '$',
            '{feeAmount}': testAmount,
            '{title}': formData.translations?.title?.ru || 'Заголовок',
            '{instruction}': formData.translations?.instruction?.ru || 'Инструкция',
            '{nameLabel}': formData.translations?.nameLabel?.ru || 'Имя:',
            '{bankLabel}': formData.translations?.bankLabel?.ru || 'Банк:',
            '{accountLabel}': formData.translations?.accountLabel?.ru || 'Счет:',
            '{amountLabel}': formData.translations?.amountLabel?.ru || 'Сумма:',
            '{dateLabel}': formData.translations?.dateLabel?.ru || 'Дата:',
            '{cancelButton}': formData.translations?.cancelButton?.ru || 'Отмена',
            '{paidButton}': formData.translations?.paidButton?.ru || 'Я заплатил',
            '{closeButton}': formData.translations?.closeButton?.ru || 'Закрыть',
            '{paymentRequired}': formData.translations?.paymentRequired?.ru || 'Требуется оплата:',
            '{walletTitle}': formData.translations?.walletTitle?.ru || 'Адрес кошелька:',
            '{successMessage}': formData.translations?.successMessage?.ru || 'Транзакция успешно выполнена',
            '{amountSent}': formData.translations?.amountSent?.ru || 'Сумма отправлена',
            '{qrCode}': '<div style="width: 120px; height: 120px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; margin: 10px auto;">QR Code</div>'
        };

        Object.entries(replacements).forEach(([key, value]) => {
            html = html.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), value);
        });
        
        // Заменяем специфичные переменные для этого модала (например, {newAmount})
        if (formData.actionKey) {
            html = html
                .replace(new RegExp(modalSpecificAmountVar.replace(/[{}]/g, '\\$&'), 'gi'), testAmount)
                .replace(new RegExp(modalSpecificAmountVarLower.replace(/[{}]/g, '\\$&'), 'gi'), testAmount)
                .replace(new RegExp(modalSpecificAmountVarUpper.replace(/[{}]/g, '\\$&'), 'gi'), testAmount);
        }

        return (
            <div className={s.previewContainer} ref={previewRef}>
                <style>{css}</style>
                <div dangerouslySetInnerHTML={{ __html: html }} />
            </div>
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.actionKey) {
            notifyWarning('Заполните название и Action Key');
            return;
        }

        if (!formData.htmlTemplate || !formData.htmlTemplate.trim()) {
            notifyWarning('Заполните HTML шаблон');
            return;
        }
        
        // CSS не обязателен - может быть пустым или внутри HTML

        try {
            // Генерируем уникальный ID если его нет
            const templateToSave = {
                ...formData,
                id: formData.id || `customModal_${Date.now()}`,
                createdAt: template?.createdAt || new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            // Вызываем callback для сохранения
            await onSave(templateToSave);
        } catch (err) {
            console.error('Ошибка сохранения шаблона:', err);
            notifyError('Ошибка сохранения шаблона: ' + err.message);
        }
    };

    return (
        <div 
            className={s.modalEditorOverlay} 
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className={s.modalEditor} onClick={(e) => e.stopPropagation()}>
                <div className={s.editorHeader}>
                    <h3>{template ? 'Редактирование модала' : 'Создание нового модала'}</h3>
                    <button 
                        className={s.closeBtn} 
                        onClick={onClose}
                        type="button"
                    >
                        ✕
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={s.editorTabs}>
                        <button
                            type="button"
                            className={`${s.editorTab} ${activeTab === 'basic' ? s.active : ''}`}
                            onClick={() => setActiveTab('basic')}
                        >
                            Основные
                        </button>
                        <button
                            type="button"
                            className={`${s.editorTab} ${activeTab === 'html' ? s.active : ''}`}
                            onClick={() => setActiveTab('html')}
                        >
                            HTML
                        </button>
                        <button
                            type="button"
                            className={`${s.editorTab} ${activeTab === 'css' ? s.active : ''}`}
                            onClick={() => setActiveTab('css')}
                        >
                            CSS
                        </button>
                        <button
                            type="button"
                            className={`${s.editorTab} ${activeTab === 'preview' ? s.active : ''}`}
                            onClick={() => setActiveTab('preview')}
                        >
                            Предпросмотр
                        </button>
                        <button
                            type="button"
                            className={`${s.editorTab} ${activeTab === 'variables' ? s.active : ''}`}
                            onClick={() => setActiveTab('variables')}
                        >
                            Переменные
                        </button>
                    </div>

                    <div className={s.editorContent}>
                        {activeTab === 'basic' && (
                            <div className={s.tabContent}>
                                <div className={s.formGroup}>
                                    <label className={s.label}>
                                        Выберите шаблон или создайте свой
                                    </label>
                                    <select
                                        className={s.select}
                                        value={formData.selectedTemplate}
                                        onChange={(e) => handleTemplateSelect(e.target.value)}
                                    >
                                        <option value="custom">Создать свой</option>
                                        <option value="commission">Комиссия</option>
                                        <option value="success">Успех</option>
                                    </select>
                                </div>

                                <div className={s.formGroup}>
                                    <label className={s.label}>
                                        Название модала <span className={s.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={s.input}
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Например: Дополнительная комиссия"
                                        required
                                    />
                                </div>

                                <div className={s.formGroup}>
                                    <label className={s.label}>
                                        Action Key (уникальный ключ) <span className={s.required}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className={s.input}
                                        value={formData.actionKey}
                                        onChange={(e) => setFormData(prev => ({ ...prev, actionKey: e.target.value }))}
                                        placeholder="Например: additionalFee"
                                        required
                                    />
                                    <div className={s.helpText}>
                                        Будет использоваться для создания функций и состояний: show{formData.actionKey || 'Modal'}
                                    </div>
                                </div>

                                <div className={s.formGroup}>
                                    <label className={s.label}>
                                        Иконка (эмодзи или путь к файлу)
                                    </label>
                                    <input
                                        type="text"
                                        className={s.input}
                                        value={formData.icon}
                                        onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                                        placeholder="💳 или /path/to/icon.png"
                                    />
                                </div>

                                <div className={s.formGroup}>
                                    <label className={s.label}>
                                        Приоритет (1-20)
                                    </label>
                                    <input
                                        type="number"
                                        className={s.input}
                                        value={formData.priority}
                                        onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                                        min="1"
                                        max="20"
                                    />
                                </div>

                                <div className={s.formGroup}>
                                    <label className={s.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.enabled}
                                            onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
                                        />
                                        <span>Включено (показывать в админке)</span>
                                    </label>
                                </div>

                                <div className={s.formGroup}>
                                    <label className={s.checkboxLabel}>
                                        <input
                                            type="checkbox"
                                            checked={formData.emailEnabled}
                                            onChange={(e) => setFormData(prev => ({ ...prev, emailEnabled: e.target.checked }))}
                                        />
                                        <span>Отправка писем включена</span>
                                    </label>
                                </div>

                                <div className={s.formGroup}>
                                    <label className={s.label}>
                                        Использовать в sendButtonBehavior
                                    </label>
                                    <select
                                        className={s.select}
                                        value={formData.sendButtonBehavior}
                                        onChange={(e) => setFormData(prev => ({ ...prev, sendButtonBehavior: e.target.value }))}
                                    >
                                        <option value="">Не использовать</option>
                                        <option value={`show${formData.actionKey.charAt(0).toUpperCase() + formData.actionKey.slice(1)}`}>
                                            show{formData.actionKey || 'Modal'}
                                        </option>
                                    </select>
                                </div>

                                <div className={s.formGroup}>
                                    <label className={s.label}>
                                        Название поля в Финансах (для переменной {`{feeAmount}`})
                                    </label>
                                    <input
                                        type="text"
                                        className={s.input}
                                        value={formData.financeFieldName}
                                        onChange={(e) => setFormData(prev => ({ ...prev, financeFieldName: e.target.value }))}
                                        placeholder="Например: Дополнительная комиссия"
                                    />
                                    <div className={s.helpText}>
                                        Если указано, будет создано поле в SiteSettings → Финансы для всех тарифов. 
                                        <br /><br />
                                        <strong>В HTML шаблоне используйте одну из переменных:</strong>
                                        <br />• {`{feeAmount}`} - универсальная переменная (автоматически подтянет значение для этого модала)
                                        {formData.actionKey && (
                                            <>
                                                <br />• {`{${formData.actionKey}Amount}`} - специфичная переменная для этого модала
                                                <br />• {`{${formData.actionKey.toLowerCase()}Amount}`} - вариант в нижнем регистре
                                            </>
                                        )}
                                        <br /><br />
                                        <strong>Как это работает:</strong> Каждый модал автоматически использует свое поле в Финансах на основе actionKey.
                                        {formData.actionKey && (
                                            <div style={{ marginTop: '8px', fontWeight: 'bold', color: '#2563eb' }}>
                                                Для модала с actionKey="{formData.actionKey}" будет создано поле "tariff{formData.actionKey.charAt(0).toUpperCase() + formData.actionKey.slice(1)}Amounts" в Финансах, и переменная {`{feeAmount}`} автоматически подтянет значение из этого поля для текущего тарифа.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'html' && (
                            <div className={s.tabContent}>
                                <VariablesPanel onInsertVariable={insertVariable} variables={availableVariables} />
                                <div className={s.formGroup}>
                                    <label className={s.label}>
                                        HTML шаблон <span className={s.required}>*</span>
                                    </label>
                                    <textarea
                                        ref={htmlTextareaRef}
                                        name="htmlTemplate"
                                        className={s.codeEditor}
                                        value={formData.htmlTemplate}
                                        onChange={(e) => setFormData(prev => ({ ...prev, htmlTemplate: e.target.value }))}
                                        rows={20}
                                        placeholder="Введите HTML код модала..."
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {activeTab === 'css' && (
                            <div className={s.tabContent}>
                                <div className={s.formGroup}>
                                    <label className={s.label}>
                                        CSS стили
                                    </label>
                                    <textarea
                                        ref={cssTextareaRef}
                                        name="cssStyles"
                                        className={s.codeEditor}
                                        value={formData.cssStyles}
                                        onChange={(e) => setFormData(prev => ({ ...prev, cssStyles: e.target.value }))}
                                        rows={20}
                                        placeholder="Введите CSS стили..."
                                    />
                                </div>
                            </div>
                        )}

                        {activeTab === 'preview' && (
                            <div className={s.tabContent}>
                                <div className={s.previewControls}>
                                    <h4>Тестовые данные для предпросмотра:</h4>
                                    <div className={s.previewInputs}>
                                        <input
                                            type="text"
                                            placeholder="Имя"
                                            value={previewData.name}
                                            onChange={(e) => setPreviewData(prev => ({ ...prev, name: e.target.value }))}
                                            className={s.previewInput}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Банк"
                                            value={previewData.bank}
                                            onChange={(e) => setPreviewData(prev => ({ ...prev, bank: e.target.value }))}
                                            className={s.previewInput}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Сумма"
                                            value={previewData.withdrawalAmount}
                                            onChange={(e) => setPreviewData(prev => ({ ...prev, withdrawalAmount: e.target.value }))}
                                            className={s.previewInput}
                                        />
                                    </div>
                                </div>
                                {renderPreview()}
                            </div>
                        )}

                        {activeTab === 'variables' && (
                            <div className={s.tabContent}>
                                <VariablesPanel onInsertVariable={insertVariable} variables={availableVariables} />
                            </div>
                        )}
                    </div>

                    <div className={s.editorFooter}>
                        <button type="button" className={s.cancelBtn} onClick={onClose} disabled={saving}>
                            Отмена
                        </button>
                        <button type="submit" className={s.saveBtn} disabled={saving}>
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Компонент панели переменных
const VariablesPanel = ({ onInsertVariable, variables }) => {
    const [showPanel, setShowPanel] = useState(true);
    const groupedVars = variables.reduce((acc, variable) => {
        if (!acc[variable.category]) {
            acc[variable.category] = [];
        }
        acc[variable.category].push(variable);
        return acc;
    }, {});

    return (
        <div className={s.variablesPanel}>
            <button
                type="button"
                className={s.variablesToggle}
                onClick={() => setShowPanel(!showPanel)}
            >
                {showPanel ? '▼' : '▶'} Доступные переменные ({variables.length})
            </button>
            {showPanel && (
                <div className={s.variablesList}>
                    {Object.entries(groupedVars).map(([category, vars]) => (
                        <div key={category} className={s.variableCategory}>
                            <div className={s.categoryTitle}>{category}</div>
                            <div className={s.variablesGrid}>
                                {vars.map((variable, index) => (
                                    <div
                                        key={index}
                                        className={s.variableItem}
                                        title={variable.description}
                                        onClick={() => onInsertVariable && onInsertVariable(variable.name)}
                                        style={{ cursor: onInsertVariable ? 'pointer' : 'default' }}
                                    >
                                        <code className={s.variableName}>{variable.name}</code>
                                        <span className={s.variableDescription}>{variable.description}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ModalStepsTab;

